import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Col, Flex, Form, message, Row, Space, Tooltip } from 'antd';
import { useDispatch, useIntl, useSelector } from 'umi';
import ProForm, { ProFormText } from '@ant-design/pro-form';
import InfoCard from './InfoCard';
import LeadLocale from '@/components/Lead/LeadLocale';
import { F, getIntlTranslation, INTL_ACTION } from '@/utils/intl';
import { useCustomerRequiredFields } from '@/utils/hooks';
import ExtraFieldSelector from '@/components/ExtraFields/ExtraFieldSelector';
import DynamicExtraFieldsForm from '@/components/ExtraFields/ExtraFieldDynamicForm';
import { ExtraFieldTypeModel } from '@/typings/models/ExtraFields';
import { setExtraFieldValueRequest } from '@/services/api/extraFieldType';
import { selectExtraFieldTypesList } from '@/selectors/extraFieldsSelectors';
import { mergeAutoAttachedExtraFields } from '@/utils/extraFields';
import isEmpty from 'lodash/isEmpty';
import {
  BankOutlined,
  HomeOutlined,
  IdcardOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  StarOutlined,
  UserDeleteOutlined,
  UserOutlined,
} from '@ant-design/icons';
import PhoneNumberInput from '@/components/format/PhoneNumberInput';
import { LeadDetailsModel, LeadModel } from '@/typings/models/Lead';
import { getLeadTitle } from '@/utils/lead';
import { FormatDate } from '@/components/PrettyDate';
import { ContactModel, CUSTOMER_TYPES, CustomerType } from '@/typings/models/Contact';
import ContactFields, { ContactFieldName } from '@/components/Contacts/ContactFields';
import { useContactRoleSuggestions } from '@/components/Contacts/useContactRoleSuggestions';
import {
  CONTACT_ENTITY,
  getContactCustomer,
  getContactName,
  getCustomerLabel,
  getHouseholdLabel,
  getOtherLeadsOfCustomer,
  isHousehold,
  prefillFromSearch,
} from '@/utils/contacts';
import ContactTagBar from '../Contacts/ContactTagBar';
import ContactTag from '../Contacts/ContactTag';
import ContactSearchPanel from '../Contacts/ContactSearchPanel';
import ContactRoleStep from '../Contacts/ContactRoleStep';
import NewContactStep from '../Contacts/NewContactStep';
import CompanyNameRow from '../Contacts/CompanyNameRow';
import ActionsMenu, { ActionsMenuItem, ActionsMenuSection } from '@/components/Contacts/ActionsMenu';
import { useLeadContacts } from '../Contacts/useLeadContacts';
import { useIsMobile } from '@/hooks/useResponsive';
import contactStyles from '../Contacts/index.module.less';
import styles from './CustomerInfoCard.less';

export interface CustomerInfoCardProps {
  customerInfo: any;
  leadId: string;
  lead: LeadDetailsModel;
  extraFields?: any[];
  onRefresh?: () => void;
}

type AddMode = 'search' | 'role' | 'create' | null;

const CustomerInfoCard: React.FC<CustomerInfoCardProps> = ({
  customerInfo,
  leadId,
  lead,
  extraFields = [],
  onRefresh,
}) => {
  const [form] = Form.useForm();
  const [contactForm] = Form.useForm();
  const dispatch = useDispatch();
  const intl = useIntl();
  const [loading, setLoading] = React.useState(false);
  const allExtraFieldTypes = useSelector(selectExtraFieldTypesList);
  const isMobile = useIsMobile();

  const {
    contacts,
    busy,
    setPrimary,
    remove,
    attach,
    createAndAttach,
    updateField,
  } = useLeadContacts(lead, onRefresh);
  // Projects from a backend without B2B contacts keep the single-person form. An
  // empty list is real: an organization created from its company name alone.
  const hasContacts = Array.isArray(lead?.contacts);
  const primaryId = lead?.primary_contact_id || contacts[0]?.id;
  const isOrganization = customerInfo?.type === CUSTOMER_TYPES.ORGANIZATION;

  const [selectedId, setSelectedId] = useState<string | undefined>(primaryId);
  const [addMode, setAddMode] = useState<AddMode>(null);
  const [term, setTerm] = useState('');
  const [picked, setPicked] = useState<ContactModel | null>(null);
  const [pendingRole, setPendingRole] = useState('');
  // Switched to company, but the required company name isn't entered yet.
  const [switchingToCompany, setSwitchingToCompany] = useState(false);
  const showCompany = isOrganization || switchingToCompany;
  const household = !showCompany && isHousehold(customerInfo, contacts);
  const householdLabel = household ? getHouseholdLabel(intl, customerInfo, contacts) : '';
  // Unknown until the linked leads have loaded.
  const otherProjectCount = useMemo(
    () => (lead?.linkedLeads ? getOtherLeadsOfCustomer(customerInfo?.id, lead.linkedLeads, leadId).length : undefined),
    [customerInfo?.id, lead?.linkedLeads, leadId],
  );

  const selected = useMemo(
    () =>
      contacts.find((contact) => contact.id === selectedId) ||
      contacts.find((contact) => contact.id === primaryId) ||
      contacts[0],
    [contacts, selectedId, primaryId],
  );
  const roleSuggestions = useContactRoleSuggestions(contacts);

  useEffect(() => {
    setAddMode(null);
    setSelectedId(undefined);
    setSwitchingToCompany(false);
  }, [leadId]);

  const editCustomer = useCallback(
    (fields: Record<string, string>) =>
      dispatch({
        type: 'leads/edit',
        payload: {
          leadId,
          ...Object.fromEntries(Object.entries(fields).map(([field, value]) => [`customer.${field}`, value])),
        },
      }),
    [dispatch, leadId],
  );

  const changeCustomerType = useCallback(
    (type: CustomerType) => {
      if (type === CUSTOMER_TYPES.ORGANIZATION) {
        // Always ask for the name: a stored one may be a household's, not a company's.
        setSwitchingToCompany(true);
        return;
      }
      setSwitchingToCompany(false);
      if (isOrganization) editCustomer({ type });
    },
    [editCustomer, isOrganization],
  );

  // The type belongs to the customer: it asks first when it reaches other projects (or may: still loading).
  const typeTarget = showCompany ? CUSTOMER_TYPES.PERSON : CUSTOMER_TYPES.ORGANIZATION;
  const typeLabel =
    typeTarget === CUSTOMER_TYPES.ORGANIZATION
      ? intl.formatMessage({ id: 'component.contacts.changeToCompany', defaultMessage: 'Change to company' })
      : intl.formatMessage({ id: 'component.contacts.changeToPerson', defaultMessage: 'Change to person' });
  // Backing out of an unsaved switch touches no project.
  const typeChangeReach = switchingToCompany ? 0 : otherProjectCount;
  const typeMenuItem: ActionsMenuItem = {
    key: 'type',
    icon: typeTarget === CUSTOMER_TYPES.ORGANIZATION ? <BankOutlined /> : <UserOutlined />,
    label: typeLabel,
    confirm:
      typeChangeReach === 0
        ? undefined
        : {
            title: typeLabel,
            content:
              typeChangeReach === undefined ? (
                <F
                  id="pages.lead.customer_information_tooltip"
                  defaultMessage="Customers can have multiple Projects. When changing Customer data, it will change for all Projects."
                />
              ) : (
                <F
                  id="component.contacts.typeChangeOtherProjects"
                  defaultMessage="This also changes {count, plural, one {# other project} other {# other projects}} of the customer."
                  values={{ count: typeChangeReach }}
                />
              ),
          },
    onSelect: () => changeCustomerType(typeTarget),
  };

  const saveCompanyName = useCallback(
    (companyName: string) => {
      editCustomer(
        switchingToCompany
          ? { type: CUSTOMER_TYPES.ORGANIZATION, company_name: companyName }
          : { company_name: companyName },
      );
      setSwitchingToCompany(false);
    },
    [editCustomer, switchingToCompany],
  );

  const attachedExtraFields = useMemo(() => {
    const withValues = extraFields.filter((field) => !isEmpty(field.values));
    return mergeAutoAttachedExtraFields(withValues, allExtraFieldTypes, 'customer');
  }, [extraFields, allExtraFieldTypes]);

  useEffect(() => {
    dispatch({ type: 'extraFields/fetchExtraFieldTypes' });
  }, [dispatch]);

  useEffect(() => {
    if (customerInfo && !hasContacts) {
      form.setFieldsValue({
        firstname: customerInfo.firstname || '',
        lastname: customerInfo.lastname || '',
        email: customerInfo.email || '',
        locale: customerInfo.locale || '',
      });
    }
  }, [customerInfo, form, hasContacts]);

  const selectedFieldIds = useMemo(() => {
    return attachedExtraFields.map((field) => field.uuid);
  }, [attachedExtraFields]);

  const handleFieldSelect = async (field: ExtraFieldTypeModel) => {
    setLoading(true);
    try {
      await setExtraFieldValueRequest({
        params: { extraFieldTypeId: field.uuid, leadId },
        data: { value: field.field_type === 'bool' ? false : null },
      });
      if (onRefresh) onRefresh();
    } catch (error) {
      message.error(
        intl.formatMessage({
          id: 'pages.extraFieldType.addedError',
          defaultMessage: 'Error adding field',
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const handleFieldSubmit = async (fieldName: string, value: any) => {
    setLoading(true);
    try {
      dispatch({
        type: 'leads/edit',
        payload: { leadId, [`customer.${fieldName}`]: value },
        then: () => {
          setLoading(false);
          if (onRefresh) onRefresh();
        },
      });
    } catch (error) {
      setLoading(false);
    }
  };

  // region Add flow
  const closeAddFlow = useCallback(() => {
    setAddMode(null);
    setPicked(null);
    setTerm('');
    setPendingRole('');
  }, []);

  const openSearch = useCallback(() => {
    setAddMode('search');
    setPicked(null);
    setPendingRole('');
  }, []);

  // Someone of the partner who isn't the customer's yet: the role is the one they'll have here.
  const pickContact = useCallback((contact: ContactModel) => {
    setPicked(contact);
    setPendingRole('');
    setAddMode('role');
  }, []);

  const linkExisting = useCallback(
    (contact: ContactModel) => {
      if (contacts.some((item) => item.id === contact.id)) {
        setSelectedId(contact.id);
        closeAddFlow();
        return;
      }
      pickContact(contact);
    },
    [closeAddFlow, contacts, pickContact],
  );

  const finishAdding = (added: boolean, contactId?: string) => {
    if (!added) return;
    if (contactId) setSelectedId(contactId);
    closeAddFlow();
  };
  // endregion

  // The ⋯ menu's open confirmation, also opened from a contact's context menu.
  const [confirmKey, setConfirmKey] = useState<string | null>(null);
  const requestRemove = useCallback((contact: ContactModel) => {
    closeAddFlow();
    setSelectedId(contact.id);
    setConfirmKey('remove');
  }, [closeAddFlow]);
  const removeContact = useCallback(
    (contact: ContactModel) => {
      remove(contact).then((removed) => {
        if (removed) setSelectedId(undefined);
      });
    },
    [remove],
  );

  const openLead = useCallback(
    (otherLeadId: string) =>
      dispatch({ type: 'leads/toggleLeadDetailsDrawer', payload: { lead: { id: otherLeadId } } }),
    [dispatch],
  );

  const openContact = useCallback(
    (contact: ContactModel) =>
      dispatch({
        type: 'contacts/openDrawer',
        payload: { contactId: contact.id, customerId: customerInfo?.id, leadId },
      }),
    [dispatch, customerInfo?.id, leadId],
  );


  const selectedValues = useMemo(() => ({ ...selected }), [selected]);

  // A private customer with one contact looks like before contacts existed:
  // no tags, roles or primary marks — only the add button, up in the title.
  const solo = !showCompany && contacts.length <= 1 && !addMode;

  // The customer's primary contact of a person customer *is* the customer: its
  // name and the partner's required customer fields stay required.
  const partnerRequiredFields = useCustomerRequiredFields();
  const selectedRequiredFields = useMemo<ContactFieldName[]>(
    () =>
      !isOrganization && selected?.is_primary
        ? [
            'firstname',
            'lastname',
            ...(['email', 'phone', 'locale', 'gender'] as ContactFieldName[]).filter((field) =>
              partnerRequiredFields.includes(field),
            ),
          ]
        : [],
    [isOrganization, selected?.is_primary, partnerRequiredFields],
  );

  // A step opening further down the card is brought into view.
  const contactAreaRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (addMode) contactAreaRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [addMode]);


  const pendingTag =
    addMode === 'role' && picked ? (
      <ContactTag contact={picked} pending roleLabel={pendingRole || '…'} />
    ) : addMode === 'create' ? (
      <ContactTag contact={{}} placeholder roleLabel={pendingRole || null} />
    ) : undefined;

  const renderContactArea = () => {
    if (addMode === 'search') {
      return (
        <ContactSearchPanel
          customerId={customerInfo?.id}
          term={term}
          onTermChange={setTerm}
          onPick={pickContact}
          onCreate={() => {
            setPendingRole('');
            setAddMode('create');
          }}
          onCancel={closeAddFlow}
        />
      );
    }
    if (addMode === 'role' && picked) {
      return (
        <ContactRoleStep
          contact={picked}
          companyLabel={getCustomerLabel(getContactCustomer(picked))}
          roleSuggestions={roleSuggestions}
          role={pendingRole}
          onRoleChange={setPendingRole}
          busy={busy}
          onChangeContact={() => setAddMode('search')}
          onCancel={closeAddFlow}
          onSubmit={({ role, makePrimary }) =>
            attach(picked, { role, makePrimary }).then((added) => finishAdding(added, picked.id))
          }
        />
      );
    }
    if (addMode === 'create') {
      return (
        <NewContactStep
          initialValues={prefillFromSearch(term)}
          customerId={customerInfo?.id}
          knownContacts={contacts}
          roleSuggestions={roleSuggestions}
          busy={busy}
          onRoleChange={setPendingRole}
          onBack={() => setAddMode('search')}
          onLinkInstead={linkExisting}
          onSubmit={(data) => createAndAttach(data).then((id) => finishAdding(Boolean(id), id || undefined))}
        />
      );
    }
    if (!selected) return null;
    return (
        <ContactFields
          key={selected.id}
          form={contactForm}
          values={selectedValues}
          onFieldSave={(field, value) => updateField(selected, field, value)}
          requiredFields={selectedRequiredFields}
          showRole={!solo}
          roleSuggestions={roleSuggestions}
        />
    );
  };

  // One ⋯ menu: the selected contact's actions, then the customer's.
  const contactMenuItems: ActionsMenuItem[] =
    selected && !addMode
      ? [
          ...(selected.id === primaryId
            ? []
            : [
                {
                  key: 'primary',
                  icon: <StarOutlined />,
                  label: intl.formatMessage({ id: 'component.contacts.setPrimary', defaultMessage: 'Set as primary contact' }),
                  disabled: busy,
                  onSelect: () => setPrimary(selected),
                },
              ]),
          {
            key: 'open',
            icon: <IdcardOutlined />,
            label: getIntlTranslation(intl, { entity: CONTACT_ENTITY, action: INTL_ACTION.VIEW }),
            onSelect: () => openContact(selected),
          },
          {
            key: 'remove',
            icon: <UserDeleteOutlined />,
            label: intl.formatMessage({ id: 'component.contacts.removeFromCustomer', defaultMessage: 'Remove from customer' }),
            danger: true,
            // The backend keeps a customer's primary contact.
            disabled: busy || Boolean(selected.is_primary),
            hint: selected.is_primary && (
              <F
                id="component.contacts.removePrimaryHint"
                defaultMessage="Make another contact the customer's primary contact before removing this one."
              />
            ),
            confirm: {
              title: intl.formatMessage({ id: 'component.contacts.removeFromCustomer', defaultMessage: 'Remove from customer' }),
              content: (
                <F
                  id="component.contacts.removeConfirm"
                  defaultMessage="{name} is removed from all projects of {customer}. Someone who isn't a contact of another customer is deleted."
                  values={{ name: <b>{getContactName(selected)}</b>, customer: getCustomerLabel(customerInfo) }}
                />
              ),
            },
            onSelect: () => removeContact(selected),
          },
        ]
      : [];
  const addContactLabel = getIntlTranslation(intl, { entity: CONTACT_ENTITY, action: INTL_ACTION.ADD });
  const menuSections: ActionsMenuSection[] = [
    { key: 'contact', title: selected && getContactName(selected), items: solo ? [] : contactMenuItems },
    { key: 'customer', items: [typeMenuItem] },
  ];

  return (
    <InfoCard
      title={
        <Flex className={styles.fullWidth} align={'center'} justify={'space-between'}>
          <Space>
            {isOrganization ? <BankOutlined /> : household ? <HomeOutlined /> : <UserOutlined />}
            <F id={'pages.lead.customer_information'} defaultMessage={'Customer Information'} />
          </Space>
          <Space size={4}>
          {hasContacts && solo && (
            // Icon-only on phones, where the label would wrap the title.
            <Tooltip title={isMobile ? addContactLabel : undefined}>
              <Button
                className={styles.titleAddButton}
                type="link"
                size="small"
                icon={<PlusOutlined />}
                aria-label={addContactLabel}
                onClick={openSearch}
              >
                {!isMobile && addContactLabel}
              </Button>
            </Tooltip>
          )}
          {hasContacts && (
            <ActionsMenu sections={menuSections} confirmKey={confirmKey} onConfirmKeyChange={setConfirmKey} />
          )}
          </Space>
        </Flex>
      }
      variant="blue"
    >
      {hasContacts ? (
        <>
          {showCompany && (
            <CompanyNameRow
              value={customerInfo?.company_name}
              pending={switchingToCompany}
              onSave={saveCompanyName}
              onCancel={() => setSwitchingToCompany(false)}
            />
          )}
          {household && (
            <CompanyNameRow
              value={householdLabel}
              optional
              label={intl.formatMessage({ id: 'component.contacts.household', defaultMessage: 'Household' })}
              icon={<HomeOutlined />}
              onSave={(name) => editCustomer({ company_name: name })}
            />
          )}
          <div className={contactStyles.contactArea}>
            {!solo && (
              <ContactTagBar
                contacts={contacts}
                selectedId={selected?.id}
                primaryId={primaryId}
                onSelect={(contact) => {
                  closeAddFlow();
                  setSelectedId(contact.id);
                }}
                onAdd={addMode === 'search' ? closeAddFlow : openSearch}
                adding={addMode === 'search'}
                pendingTag={pendingTag}
                onSetPrimary={setPrimary}
                onOpen={openContact}
                onRemove={requestRemove}
              />
            )}
            <div ref={contactAreaRef}>{renderContactArea()}</div>
          </div>
        </>
      ) : (
        <ProForm form={form} autoComplete="off" size="small" submitter={false} variant={'underlined'}>
          <Row gutter={8}>
            <Col span={12}>
              <ProFormText
                label={intl.formatMessage({ id: 'pages.customers.firstName', defaultMessage: 'First Name' })}
                name="firstname"
                rules={[{ required: true }]}
                fieldProps={{
                  onBlur: async (e) => {
                    const value = e.target.value;
                    try {
                      await form.validateFields(['firstname']);
                      if (value !== customerInfo.firstname) handleFieldSubmit('firstname', value);
                    } catch {}
                  },
                }}
              />
            </Col>
            <Col span={12}>
              <ProFormText
                label={intl.formatMessage({ id: 'pages.customers.lastName', defaultMessage: 'Last Name' })}
                name="lastname"
                rules={[{ required: true }]}
                fieldProps={{
                  onBlur: async (e) => {
                    const value = e.target.value;
                    try {
                      await form.validateFields(['lastname']);
                      if (value !== customerInfo.lastname) handleFieldSubmit('lastname', value);
                    } catch {}
                  },
                }}
              />
            </Col>
          </Row>

          <Row gutter={8}>
            <Col span={12}>
              <ProFormText
                label={intl.formatMessage({ id: 'common.attribute.email', defaultMessage: 'Email' })}
                name="email"
                rules={[{ required: true }, { type: 'email' }]}
                fieldProps={{
                  onBlur: async (e) => {
                    const value = e.target.value;
                    try {
                      await form.validateFields(['email']);
                      if (value !== customerInfo.email) handleFieldSubmit('email', value);
                    } catch {}
                  },
                }}
              />
            </Col>
            <Col span={12}>
              <ProForm.Item
                label={intl.formatMessage({ id: 'common.attribute.phone', defaultMessage: 'Phone' })}
              >
                <PhoneNumberInput
                  value={customerInfo?.phone}
                  onChange={(e164) => {
                    if (e164 !== customerInfo?.phone) handleFieldSubmit('phone', e164);
                  }}
                />
              </ProForm.Item>
            </Col>
          </Row>

          <Row gutter={8}>
            <Col span={12}>
              <ProForm.Item
                label={
                  <F id={'pages.leads.filters.locale.placeholder'} defaultMessage={'Language'} />
                }
              >
                <LeadLocale
                  lead={{ customer: customerInfo, id: leadId } as LeadDetailsModel}
                  selectProps={{ bordered: true }}
                />
              </ProForm.Item>
            </Col>
          </Row>
        </ProForm>
      )}

      {attachedExtraFields && attachedExtraFields.length > 0 && (
        <Row gutter={8}>
          <Col span={24}>
            <DynamicExtraFieldsForm
              fields={attachedExtraFields}
              leadId={leadId}
              labelWidth={240}
              formProps={{ layout: 'vertical' }}
              removable
              grid
              onRefresh={onRefresh}
            />
          </Col>
        </Row>
      )}

      <Row gutter={8}>
        <Col span={24}>
          <ExtraFieldSelector
            relatedTo="customer"
            selectedFieldIds={selectedFieldIds}
            onFieldSelect={handleFieldSelect}
            onFieldCreated={handleFieldSelect}
          />
        </Col>
      </Row>
    </InfoCard>
  );
};

export default CustomerInfoCard;
